import firebase from 'firebase/compat/app';
import moment from 'moment-timezone';
import pickBy from "lodash-es/pickBy"

export interface ServicePlan {
  name: string;
  wholesale_setup_price: number;
  wholesale_recur_price: number;
  msrp_setup_price: number;
  msrp_recur_price: number;
  highlights: string[];
  trial_days: number;
  min_duration_months: number;
  deleted: boolean;
  stripe_plan_id: string;
  stripe_sku_id: string;
}
export interface ServiceFAQ {
  que: string;
  ans: string;
  deleted: boolean;
}
export interface ServiceFiles {
  file_name: string;
  file_url: string;
  deleted: boolean;
}

export interface AgencyServiceData {
  deleted?: boolean;
  date_added?: firebase.firestore.Timestamp;
  date_updated?: firebase.firestore.Timestamp;
  name?: string;
  icon_url?: string;
  icon_bgcolor_name?: string;
  video_url?: string;
  overview?: string;
  description?: string;
  highlights?: string[];
  faqs?: ServiceFAQ[];
  files?: ServiceFiles[];
  plans?: ServicePlan[];
  beta_only?: boolean;
  agency_level?: boolean;
  agency_addon_type?: string;
  [key: string]: any;
}

// Collection reference helper
const collectionRef = () => {
  return firebase.firestore().collection('agency_service');
};

// Create a service
export const createAgencyService = (snapshot?: firebase.firestore.DocumentSnapshot): {
  // Properties
  id: string;
  ref: firebase.firestore.DocumentReference;
  data: () => AgencyServiceData;
  
  // Getters/Setters
  getDateAdded: () => moment.Moment;
  setDateAdded: (dateAdded: moment.Moment) => void;
  getDateUpdated: () => moment.Moment;
  setDateUpdated: (dateUpdated: moment.Moment) => void;
  getDeleted: () => boolean;
  setDeleted: (deleted: boolean) => void;
  getName: () => string;
  setName: (name: string) => void;
  getIconUrl: () => string;
  setIconUrl: (iconUrl: string) => void;
  getIconBgcolorName: () => string;
  setIconBgcolorName: (iconBgcolorName: string) => void;
  getVideoUrl: () => string;
  setVideoUrl: (videoUrl: string) => void;
  getOverview: () => string;
  setOverview: (overview: string) => void;
  getDescription: () => string;
  setDescription: (description: string) => void;
  getHighlights: () => string[];
  setHighlights: (highlights: string[]) => void;
  getFaqs: () => ServiceFAQ[];
  setFaqs: (faqs: ServiceFAQ[]) => void;
  getFiles: () => ServiceFiles[];
  setFiles: (files: ServiceFiles[]) => void;
  getPlans: () => ServicePlan[];
  setPlans: (plans: ServicePlan[]) => void;
  getBetaOnly: () => boolean;
  setBetaOnly: (betaOnly: boolean) => void;
  getAgencyLevel: () => boolean;
  setAgencyLevel: (agencyLevel: boolean) => void;
  getAgencyAddonType: () => string;
  setAgencyAddonType: (agencyAddonType: string) => void;
  
  // Methods
  save: () => Promise<any>;
} => {
  let _id: string;
  let _data: AgencyServiceData = {};
  let _ref: firebase.firestore.DocumentReference;

  if (snapshot) {
    _id = snapshot.id;
    _data = snapshot.data() as AgencyServiceData || {};
    _ref = snapshot.ref;
  } else {
    _ref = collectionRef().doc();
    _id = _ref.id;
    _data = {};
    _data.deleted = false;
    _data.date_added = firebase.firestore.Timestamp.fromMillis(moment.utc().valueOf());
  }

  return {
    // Properties
    id: _id,
    ref: _ref,
    data: () => pickBy(_data, (v) => v !== null && v !== undefined),
    
    // Getters/Setters
    getDateAdded: () => _data.date_added ? moment.utc(_data.date_added.toMillis()) : moment.utc(),
    setDateAdded: (dateAdded: moment.Moment) => {
      _data.date_added = firebase.firestore.Timestamp.fromMillis(dateAdded.valueOf());
    },
    
    getDateUpdated: () => _data.date_updated ? moment.utc(_data.date_updated.toMillis()) : moment.utc(),
    setDateUpdated: (dateUpdated: moment.Moment) => {
      _data.date_updated = firebase.firestore.Timestamp.fromMillis(dateUpdated.valueOf());
    },
    
    getDeleted: () => !!_data.deleted,
    setDeleted: (deleted: boolean) => { _data.deleted = deleted },
    
    getName: () => _data.name as string,
    setName: (name: string) => { _data.name = name },
    
    getIconUrl: () => _data.icon_url as string,
    setIconUrl: (iconUrl: string) => { _data.icon_url = iconUrl },
    
    getIconBgcolorName: () => _data.icon_bgcolor_name as string,
    setIconBgcolorName: (iconBgcolorName: string) => { _data.icon_bgcolor_name = iconBgcolorName },
    
    getVideoUrl: () => _data.video_url as string,
    setVideoUrl: (videoUrl: string) => { _data.video_url = videoUrl },
    
    getOverview: () => _data.overview as string,
    setOverview: (overview: string) => { _data.overview = overview },
    
    getDescription: () => _data.description as string,
    setDescription: (description: string) => { _data.description = description },
    
    getHighlights: () => _data.highlights as string[],
    setHighlights: (highlights: string[]) => { _data.highlights = highlights },
    
    getFaqs: () => _data.faqs as ServiceFAQ[],
    setFaqs: (faqs: ServiceFAQ[]) => { _data.faqs = faqs },
    
    getFiles: () => _data.files as ServiceFiles[],
    setFiles: (files: ServiceFiles[]) => { _data.files = files },
    
    getPlans: () => _data.plans as ServicePlan[],
    setPlans: (plans: ServicePlan[]) => { _data.plans = plans },
    
    getBetaOnly: () => !!_data.beta_only,
    setBetaOnly: (betaOnly: boolean) => { _data.beta_only = betaOnly },
    
    getAgencyLevel: () => !!_data.agency_level,
    setAgencyLevel: (agencyLevel: boolean) => { _data.agency_level = agencyLevel },
    
    getAgencyAddonType: () => _data.agency_addon_type as string,
    setAgencyAddonType: (agencyAddonType: string) => { _data.agency_addon_type = agencyAddonType },
    
    // Methods
    save: async () => {
      _data.date_updated = firebase.firestore.Timestamp.fromMillis(moment.utc().valueOf());
      await _ref.set(pickBy(_data, (v) => v !== null && v !== undefined));
      return { id: _id, ref: _ref, data: _data };
    }
  };
};

// Static methods as standalone functions
export const getById = async (id: string): Promise<ReturnType<typeof createAgencyService> | undefined> => {
  const snapshot = await collectionRef().doc(id).get();
  if (!snapshot.exists || (snapshot.data() as AgencyServiceData)?.deleted) return undefined;
  return createAgencyService(snapshot);
};

export const getAllServices = async (): Promise<ReturnType<typeof createAgencyService>[]> => {
  const snapshot = await collectionRef().where('deleted', '==', false).get();
  return snapshot.docs.map((d) => createAgencyService(d));
};

// For backward compatibility with code that uses "new AgencyService()"
export default class AgencyService {
  public static collectionRef = collectionRef;
  public static getById = getById;
  public static getAllServices = getAllServices;

  private _service: ReturnType<typeof createAgencyService>;

  constructor(snapshot?: firebase.firestore.DocumentSnapshot) {
    this._service = createAgencyService(snapshot);
  }

  get id(): string { return this._service.id; }
  get ref(): firebase.firestore.DocumentReference { return this._service.ref; }
  get data(): { [key: string]: any } { return this._service.data(); }
  
  get dateAdded(): moment.Moment { return this._service.getDateAdded(); }
  set dateAdded(value: moment.Moment) { this._service.setDateAdded(value); }
  
  get dateUpdated(): moment.Moment { return this._service.getDateUpdated(); }
  set dateUpdated(value: moment.Moment) { this._service.setDateUpdated(value); }
  
  get deleted(): boolean { return this._service.getDeleted(); }
  set deleted(value: boolean) { this._service.setDeleted(value); }
  
  get name(): string { return this._service.getName(); }
  set name(value: string) { this._service.setName(value); }
  
  get iconUrl(): string { return this._service.getIconUrl(); }
  set iconUrl(value: string) { this._service.setIconUrl(value); }
  
  get iconBgcolorName(): string { return this._service.getIconBgcolorName(); }
  set iconBgcolorName(value: string) { this._service.setIconBgcolorName(value); }
  
  get videoUrl(): string { return this._service.getVideoUrl(); }
  set videoUrl(value: string) { this._service.setVideoUrl(value); }
  
  get overview(): string { return this._service.getOverview(); }
  set overview(value: string) { this._service.setOverview(value); }
  
  get description(): string { return this._service.getDescription(); }
  set description(value: string) { this._service.setDescription(value); }
  
  get highlights(): string[] { return this._service.getHighlights(); }
  set highlights(value: string[]) { this._service.setHighlights(value); }
  
  get faqs(): ServiceFAQ[] { return this._service.getFaqs(); }
  set faqs(value: ServiceFAQ[]) { this._service.setFaqs(value); }
  
  get files(): ServiceFiles[] { return this._service.getFiles(); }
  set files(value: ServiceFiles[]) { this._service.setFiles(value); }
  
  get plans(): ServicePlan[] { return this._service.getPlans(); }
  set plans(value: ServicePlan[]) { this._service.setPlans(value); }
  
  get betaOnly(): boolean { return this._service.getBetaOnly(); }
  set betaOnly(value: boolean) { this._service.setBetaOnly(value); }
  
  get agencyLevel(): boolean { return this._service.getAgencyLevel(); }
  set agencyLevel(value: boolean) { this._service.setAgencyLevel(value); }
  
  get agencyAddonType(): string { return this._service.getAgencyAddonType(); }
  set agencyAddonType(value: string) { this._service.setAgencyAddonType(value); }

  public async save(): Promise<AgencyService> {
    await this._service.save();
    return this;
  }
}
